import { ComponentProps, ComponentPropsWithoutRef, forwardRef, useImperativeHandle } from "react";
import { ControlledInputProps, OmitControlledInputProps } from "@vixi/types";
import { FileItem } from "./FileItem";
import { useTranslation } from "@vixi/localization";
import { useFileInput } from "@/hooks/useFileInput";
import { cn } from "@/lib/utils";
import { Img } from "@/components/base/img";
import { buttonVariants } from "@/components/base/button";

const FileUploadBoxRoot = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "border-primary-light relative flex max-w-full flex-wrap items-center justify-between rounded border-3 p-5 lg:h-87.5 lg:w-247.25",
        className,
      )}
      {...props}
    />
  ),
);

const FileUploadBoxUploadArea = forwardRef<HTMLDivElement, ComponentPropsWithoutRef<"div">>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      className={cn(
        "flex flex-1 flex-col items-center justify-center ps-5 text-center lg:w-auto",
        className,
      )}
      {...props}
    />
  ),
);

interface UploadContainerProps
  extends ControlledInputProps<File[]>,
    OmitControlledInputProps<React.InputHTMLAttributes<HTMLInputElement>> {
  multiple?: boolean;
  slotProps?: {
    root?: Partial<ComponentProps<typeof FileUploadBoxRoot>>;
  };
  image: { src: string; alt: string };
  imageSize?: { width: string; height: string };
  dropzoneLabel: string;
  imageMarginBlock: string;
}

export const FileUploadBox = forwardRef<HTMLInputElement, UploadContainerProps>(
  (
    {
      multiple,
      image,
      slotProps,
      imageSize = { width: "200px", height: "auto" },
      dropzoneLabel: text,
      imageMarginBlock: marginBlock,
      onChange,
      onBlur,
      name,
      disabled,
      value: files,
      ...inputProps
    }: UploadContainerProps,
    ref,
  ) => {
    const { t } = useTranslation();

    const { inputRef, handleFileChange, handleRemoveFile, appendToFileList } = useFileInput({
      multiple,
      value: files,
      onChange,
      onBlur,
    });

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
      event.preventDefault();
      const fileList = event.dataTransfer.files;
      appendToFileList(fileList);
    };

    return (
      <FileUploadBoxRoot {...slotProps?.root} onDragOver={handleDragOver} onDrop={handleDrop}>
        {/* Left side - Icon */}
        <div className="max-h-full w-50 max-w-9/10 object-contain" style={{ ...imageSize }}>
          <Img
            contain
            style={{ marginBlock }}
            src={image.src}
            alt={image.alt}
            width="100%"
            draggable="false"
          />
        </div>

        {/* Right side - Upload area */}
        <FileUploadBoxUploadArea>
          {files?.length ? (
            <div className="mb-4 flex max-h-41.25 flex-col gap-1.25 overflow-y-auto">
              {files.map((file, i) => (
                <FileItem key={i} file={file} onDelete={() => handleRemoveFile(i)} />
              ))}
            </div>
          ) : (
            <h3 className="text-[25px]/1.76 text-foreground ar:font-medium mb-2.5 font-semibold">
              {text} <br /> {t("Or")}
            </h3>
          )}

          {/* Upload button */}
          <label className={buttonVariants({ size: "medium", variant: "contained" })}>
            {t("Action.Browse")}
            <input
              {...inputProps}
              ref={inputRef}
              type="file"
              hidden
              onChange={handleFileChange}
              multiple={multiple}
            />
          </label>
        </FileUploadBoxUploadArea>
      </FileUploadBoxRoot>
    );
  },
);

export default FileUploadBox;
